import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  Chip,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Divider,
  Tooltip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar
} from '@mui/material';
import {
  Download,
  FileDownload,
  PictureAsPdf,
  TableChart,
  DataObject,
  DateRange,
  FilterList,
  Settings,
  CloudDownload,
  History,
  Info,
  CheckCircle,
  Schedule,
  Error,
  Delete,
  Refresh,
  Share,
  Email,
  Print,
  Visibility,
  GetApp,
  Assessment,
  MonitorHeart,
  Timeline,
  Analytics
} from '@mui/icons-material';

const ExportPage = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  
  const [exportOptions, setExportOptions] = useState({
    format: 'pdf',
    dateRange: 'all',
    startDate: '',
    endDate: '',
    includeReadings: true,
    includeAnalytics: true,
    includeProfile: true,
    includeMedications: false,
    includeNotes: false
  });
  
  const [isExporting, setIsExporting] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  const [exportHistory, setExportHistory] = useState([
    {
      id: 1,
      filename: 'health_data_2025-01-08.pdf',
      format: 'PDF',
      size: '2.3 MB',
      date: '2025-01-08',
      time: '14:30',
      status: 'completed',
      downloads: 1
    },
    {
      id: 2,
      filename: 'bp_readings_2025-01-07.csv',
      format: 'CSV',
      size: '156 KB',
      date: '2025-01-07',
      time: '09:15',
      status: 'completed',
      downloads: 3
    },
    {
      id: 3,
      filename: 'full_export_2025-01-06.json',
      format: 'JSON',
      size: '1.1 MB',
      date: '2025-01-06',
      time: '16:45',
      status: 'failed',
      downloads: 0
    }
  ]);
  
  const [dataStats, setDataStats] = useState({
    totalReadings: 142,
    dateRange: '2024-01-01 to 2025-01-08',
    averageSystolic: 122,
    averageDiastolic: 78,
    lastExport: '2025-01-08'
  });

  const exportFormats = [
    {
      id: 'pdf',
      name: 'PDF Report',
      description: 'Comprehensive health report with charts and analysis',
      icon: <PictureAsPdf />,
      color: 'error',
      features: ['Charts', 'Analysis', 'Professional Layout']
    },
    {
      id: 'csv',
      name: 'CSV Data',
      description: 'Spreadsheet format for data analysis',
      icon: <TableChart />,
      color: 'success',
      features: ['Raw Data', 'Excel Compatible', 'Easy Analysis']
    },
    {
      id: 'json',
      name: 'JSON Export',
      description: 'Machine-readable format for developers',
      icon: <DataObject />,
      color: 'info',
      features: ['API Compatible', 'Structured Data', 'Developer Friendly']
    }
  ];

  const handleExportOptionChange = (field, value) => {
    setExportOptions(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleExport = async () => {
    setIsExporting(true);
    
    try {
      // Simulate export process
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      const filename = `health_data_${new Date().toISOString().split('T')[0]}.${exportOptions.format}`;
      const newExport = {
        id: Date.now(),
        filename,
        format: exportOptions.format.toUpperCase(),
        size: exportOptions.format === 'pdf' ? '2.1 MB' : exportOptions.format === 'csv' ? '245 KB' : '1.3 MB',
        date: new Date().toISOString().split('T')[0],
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        status: 'completed',
        downloads: 0
      };
      
      setExportHistory(prev => [newExport, ...prev]);
      setSuccessMessage(`Export completed successfully! ${filename} is ready for download.`);
      
      // Simulate file download
      const blob = new Blob(['Sample health data export'], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
    } catch (error) {
      setErrorMessage('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handlePreview = () => {
    setPreviewDialogOpen(true);
  };

  const handleDownloadExport = (exportItem) => {
    // Simulate download
    const blob = new Blob(['Sample health data export'], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = exportItem.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    // Update download count
    setExportHistory(prev => prev.map(item => 
      item.id === exportItem.id 
        ? { ...item, downloads: item.downloads + 1 }
        : item
    ));
    
    setSuccessMessage(`Downloaded ${exportItem.filename}`);
  };

  const handleDeleteExport = (exportId) => {
    setExportHistory(prev => prev.filter(item => item.id !== exportId));
    setSuccessMessage('Export deleted successfully.');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'processing': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle color="success" />;
      case 'failed': return <Error color="error" />;
      case 'processing': return <Schedule color="warning" />;
      default: return <Info />;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" component="h1">
          <Download sx={{ mr: 1, verticalAlign: 'middle' }} />
          Export Health Data
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<Assessment />}
            onClick={() => navigate('/analytics')}
          >
            View Analytics
          </Button>
          <Button
            variant="outlined"
            startIcon={<Settings />}
            onClick={() => navigate('/settings')}
          >
            Settings
          </Button>
        </Stack>
      </Box>

      <Grid container spacing={3}>
        {/* Export Options */}
        <Grid item xs={12} md={8}>
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              <FilterList sx={{ mr: 1, verticalAlign: 'middle' }} />
              Export Options
            </Typography>
            
            {/* Format Selection */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {exportFormats.map((format) => (
                <Grid item xs={12} sm={4} key={format.id}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: exportOptions.format === format.id ? 2 : 1,
                      borderColor: exportOptions.format === format.id ? `${format.color}.main` : 'grey.300',
                      '&:hover': { borderColor: `${format.color}.main` }
                    }}
                    onClick={() => handleExportOptionChange('format', format.id)}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={1}>
                        <Avatar sx={{ bgcolor: `${format.color}.main`, mr: 1 }}>
                          {format.icon}
                        </Avatar>
                        <Typography variant="h6">{format.name}</Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {format.description}
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {format.features.map((feature) => (
                          <Chip key={feature} label={feature} size="small" variant="outlined" />
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Date Range */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Date Range</InputLabel>
                  <Select
                    value={exportOptions.dateRange}
                    label="Date Range"
                    onChange={(e) => handleExportOptionChange('dateRange', e.target.value)}
                  >
                    <MenuItem value="all">All Time</MenuItem>
                    <MenuItem value="last30">Last 30 Days</MenuItem>
                    <MenuItem value="last90">Last 90 Days</MenuItem>
                    <MenuItem value="lastyear">Last Year</MenuItem>
                    <MenuItem value="custom">Custom Range</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              {exportOptions.dateRange === 'custom' && (
                <>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Start Date"
                      type="date"
                      value={exportOptions.startDate}
                      onChange={(e) => handleExportOptionChange('startDate', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="End Date"
                      type="date"
                      value={exportOptions.endDate}
                      onChange={(e) => handleExportOptionChange('endDate', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </>
              )}
            </Grid>

            {/* Data Selection */}
            <Typography variant="subtitle1" gutterBottom>
              Data to Include
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={exportOptions.includeReadings}
                      onChange={(e) => handleExportOptionChange('includeReadings', e.target.checked)}
                    />
                  }
                  label="Blood Pressure Readings"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={exportOptions.includeAnalytics}
                      onChange={(e) => handleExportOptionChange('includeAnalytics', e.target.checked)}
                    />
                  }
                  label="Analytics & Trends"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={exportOptions.includeProfile}
                      onChange={(e) => handleExportOptionChange('includeProfile', e.target.checked)}
                    />
                  }
                  label="Profile Information"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={exportOptions.includeMedications}
                      onChange={(e) => handleExportOptionChange('includeMedications', e.target.checked)}
                    />
                  }
                  label="Medications & Notes"
                />
              </Grid>
            </Grid>

            {/* Action Buttons */}
            <Box display="flex" gap={2} mt={3}>
              <Button
                variant="contained"
                size="large"
                startIcon={<Download />}
                onClick={handleExport}
                disabled={isExporting}
              >
                {isExporting ? 'Exporting...' : 'Export Data'}
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<Visibility />}
                onClick={handlePreview}
              >
                Preview
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<Share />}
                onClick={() => setSuccessMessage('Sharing options coming soon!')}
              >
                Share
              </Button>
            </Box>

            {isExporting && (
              <Box sx={{ mt: 3 }}>
                <LinearProgress />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Preparing your export... This may take a few moments.
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Data Summary */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              <Assessment sx={{ mr: 1, verticalAlign: 'middle' }} />
              Data Summary
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <MonitorHeart color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Total Readings"
                  secondary={dataStats.totalReadings}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <DateRange color="secondary" />
                </ListItemIcon>
                <ListItemText
                  primary="Date Range"
                  secondary={dataStats.dateRange}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Timeline color="success" />
                </ListItemIcon>
                <ListItemText
                  primary="Average BP"
                  secondary={`${dataStats.averageSystolic}/${dataStats.averageDiastolic} mmHg`}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CloudDownload color="info" />
                </ListItemIcon>
                <ListItemText
                  primary="Last Export"
                  secondary={dataStats.lastExport}
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Export History */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                <History sx={{ mr: 1, verticalAlign: 'middle' }} />
                Export History
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Refresh />}
                onClick={() => setSuccessMessage('Export history refreshed')}
              >
                Refresh
              </Button>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>File</TableCell>
                    <TableCell>Format</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Downloads</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {exportHistory.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {item.filename}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={item.format} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{item.size}</TableCell>
                      <TableCell>
                        {item.date} at {item.time}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          {getStatusIcon(item.status)}
                          <Chip
                            label={item.status}
                            size="small"
                            color={getStatusColor(item.status)}
                            sx={{ ml: 1 }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>{item.downloads}</TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          {item.status === 'completed' && (
                            <Tooltip title="Download">
                              <IconButton
                                size="small"
                                onClick={() => handleDownloadExport(item)}
                              >
                                <GetApp />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteExport(item.id)}
                              color="error"
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Export Preview</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Preview for {exportOptions.format.toUpperCase()} export with {exportOptions.dateRange} date range
          </Alert>
          <Typography variant="body1">
            Your export will include:
          </Typography>
          <List>
            {exportOptions.includeReadings && (
              <ListItem>
                <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                <ListItemText primary="Blood Pressure Readings" secondary={`${dataStats.totalReadings} readings`} />
              </ListItem>
            )}
            {exportOptions.includeAnalytics && (
              <ListItem>
                <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                <ListItemText primary="Analytics & Trends" secondary="Charts and statistical analysis" />
              </ListItem>
            )}
            {exportOptions.includeProfile && (
              <ListItem>
                <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                <ListItemText primary="Profile Information" secondary="Personal details and preferences" />
              </ListItem>
            )}
            {exportOptions.includeMedications && (
              <ListItem>
                <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                <ListItemText primary="Medications & Notes" secondary="Medical history and notes" />
              </ListItem>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
          <Button variant="contained" onClick={handleExport}>
            Export Now
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Messages */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage('')}
      >
        <Alert severity="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage('')}
      >
        <Alert severity="error" onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ExportPage; 